import React, { useState, useEffect } from 'react';
import axios from 'axios';

const FluxoCaixa = ({ setCurrentView, API, getAuthHeaders }) => {
  const [lancamentos, setLancamentos] = useState([]);
  const [saldoAtual, setSaldoAtual] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editandoLancamento, setEditandoLancamento] = useState(null);
  const [filtros, setFiltros] = useState(() => {
    // Função para calcular o início do trimestre atual
    const getInicioTrimestre = () => {
      const hoje = new Date();
      const mes = hoje.getMonth(); // 0-11
      const ano = hoje.getFullYear();

      // Calcular início do trimestre
      let mesInicio;
      if (mes >= 0 && mes <= 2) mesInicio = 0; // Jan-Mar
      else if (mes >= 3 && mes <= 5) mesInicio = 3; // Abr-Jun
      else if (mes >= 6 && mes <= 8) mesInicio = 6; // Jul-Set
      else mesInicio = 9; // Out-Dez

      return new Date(ano, mesInicio, 1).toISOString().split('T')[0];
    };

    // Tentar recuperar data salva no localStorage, senão usar início do trimestre
    const dataInicioSalva = localStorage.getItem('fluxo_caixa_data_inicio');
    const dataInicioDefault = dataInicioSalva || getInicioTrimestre();

    return {
      dataInicio: dataInicioDefault,
      dataFim: new Date().toISOString().split('T')[0],
      tipo: 'todos'
    };
  });

  const [formData, setFormData] = useState({
    tipo: 'receita',
    descricao: '',
    categoria: '',
    valor: '',
    data: new Date().toISOString().split('T')[0],
    metodo_pagamento: 'dinheiro',
    observacoes: ''
  });

  const [relatorio, setRelatorio] = useState(null);
  const [categorias, setCategorias] = useState({ receita: [], despesa: [] });
  const [showCategoriaModal, setShowCategoriaModal] = useState(false);
  const [categoriaForm, setCategoriaForm] = useState({ nome: '', tipo: 'receita', descricao: '' });
  const [categoriasDisponiveis, setCategoriasDisponiveis] = useState([]);

  const metodosPagamento = ['dinheiro', 'pix', 'cartao_debito', 'cartao_credito', 'transferencia'];

  const loadCategorias = async () => {
    // Só carregar se estiver autenticado
    const token = localStorage.getItem('ebd_token');
    if (!token) return;

    try {
      const response = await axios.get(`${API}/categorias`, { headers: getAuthHeaders() });
      const cats = response.data || [];

      // Organizar categorias por tipo
      const categoriasOrganizadas = {
        receita: cats.filter(c => c.tipo === 'receita' || c.tipo === 'ambos').map(c => c.nome),
        despesa: cats.filter(c => c.tipo === 'despesa' || c.tipo === 'ambos').map(c => c.nome)
      };

      setCategorias(categoriasOrganizadas);
      setCategoriasDisponiveis(cats);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message;
      
      // Se falhar, inicializar categorias padrão
      try {
        await axios.post(`${API}/categorias/inicializar`, {}, { headers: getAuthHeaders() });
        loadCategorias(true); // Tentar novamente
      } catch (initError) {
        console.error('Erro ao inicializar categorias:', initError);
        const initErrorMsg = initError.response?.data?.detail || initError.response?.data?.message || initError.message;
        alert(`Erro ao carregar categorias: ${errorMsg}\nErro ao inicializar: ${initErrorMsg}`);
      }
    }
  };

  const handleSalvarCategoria = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/categorias`, categoriaForm, { headers: getAuthHeaders() });
      loadCategorias(true); // Recarregar categorias
      setShowCategoriaModal(false);
      setCategoriaForm({ nome: '', tipo: 'receita', descricao: '' });
      alert('Categoria criada com sucesso!');
    } catch (error) {
      console.error('Erro ao criar categoria:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message;
      alert(`Erro ao criar categoria: ${errorMsg}`);
    }
  };

  const handleExcluirCategoria = async (categoriaId) => {
    if (confirm('Tem certeza que deseja excluir esta categoria?')) {
      try {
        await axios.delete(`${API}/categorias/${categoriaId}`, { headers: getAuthHeaders() });
        loadCategorias(true); // Recarregar categorias
        alert('Categoria excluída com sucesso!');
      } catch (error) {
        console.error('Erro ao excluir categoria:', error);
        const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message;
        alert(`Erro ao excluir categoria: ${errorMsg}`);
      }
    }
  };

  const loadLancamentos = async () => {
    // Só carregar se estiver autenticado
    const token = localStorage.getItem('ebd_token');
    if (!token) return;

    setLoading(true);
    try {
      const response = await axios.get(`${API}/fluxo-caixa?data_inicio=${filtros.dataInicio}&data_fim=${filtros.dataFim}`, { headers: getAuthHeaders() });
      setLancamentos(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar lançamentos:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message;
      alert(`Erro ao carregar lançamentos: ${errorMsg}`);
      setLancamentos([]);
    } finally {
      setLoading(false);
    }
  };

  const loadRelatorio = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/fluxo-caixa/relatorio/${filtros.dataInicio}/${filtros.dataFim}`, { headers: getAuthHeaders() });
      setRelatorio(response.data);
      setSaldoAtual(response.data?.saldo_final || 0);
    } catch (error) {
      console.error('Erro ao carregar relatório:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message;
      alert(`Erro ao carregar relatório: ${errorMsg}`);
      setRelatorio(null);
      setSaldoAtual(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategorias(true); // Carregar categorias na inicialização
    loadLancamentos(true);
    loadRelatorio();
  }, [filtros.dataInicio, filtros.dataFim]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const dados = {
        ...formData,
        valor: parseFloat(formData.valor.replace(',', '.'))
      };

      if (editandoLancamento) {
        await axios.put(`${API}/fluxo-caixa/${editandoLancamento.id}`, dados, { headers: getAuthHeaders() });
        alert('Lançamento atualizado com sucesso!');
      } else {
        await axios.post(`${API}/fluxo-caixa`, dados, { headers: getAuthHeaders() });
        alert('Lançamento criado com sucesso!');
      }

      setFormData({
        tipo: 'receita',
        descricao: '',
        categoria: '',
        valor: '',
        data: new Date().toISOString().split('T')[0],
        metodo_pagamento: 'dinheiro',
        observacoes: ''
      });
      setShowForm(false);
      setEditandoLancamento(null);
      loadLancamentos(true);
      loadRelatorio();
    } catch (error) {
      console.error('Erro ao salvar lançamento:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message;
      alert(`Erro ao salvar lançamento: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const excluirLancamento = async (id) => {
    if (!confirm('Tem certeza que deseja excluir este lançamento?')) return;

    try {
      await axios.delete(`${API}/fluxo-caixa/${id}`, { headers: getAuthHeaders() });
      alert('Lançamento excluído com sucesso!');
      loadLancamentos(true);
      loadRelatorio();
    } catch (error) {
      console.error('Erro ao excluir lançamento:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message;
      alert(`Erro ao excluir lançamento: ${errorMsg}`);
    }
  };

  const editarLancamento = (lancamento) => {
    setFormData({
      tipo: lancamento.tipo,
      descricao: lancamento.descricao,
      categoria: lancamento.categoria,
      valor: lancamento.valor.toString().replace('.', ','),
      data: lancamento.data,
      metodo_pagamento: lancamento.metodo_pagamento || 'dinheiro',
      observacoes: lancamento.observacoes || ''
    });
    setEditandoLancamento(lancamento);
    setShowForm(true);
  };

  const lancamentosFiltrados = lancamentos.filter(lancamento =>
    filtros.tipo === 'todos' || lancamento.tipo === filtros.tipo
  );

  return (
    <div className="p-4 md:p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-t-lg">
          <div className="px-6 py-8">
            <div className="flex flex-col sm:flex-row items-center justify-between">
              <button
                onClick={() => setCurrentView('historico')}
                className="mb-4 sm:mb-0 px-4 py-2 bg-white/20 text-white rounded-lg hover:bg-white/30 transition-colors"
              >
                ← Voltar ao Faturamento
              </button>

              <div className="text-center sm:flex-1 sm:mx-6">
                <h1 className="text-xl sm:text-2xl font-bold">💰 Fluxo de Caixa</h1>
                <p className="text-green-100 text-sm mt-1">Controle financeiro completo da EBD</p>
              </div>

              <div className="text-center">
                <p className="text-sm text-green-100">Saldo Atual</p>
                <p className="text-2xl font-bold">R$ {saldoAtual.toFixed(2).replace('.', ',')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white p-4 sm:p-6 border-b">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Data Início</label>
              <input
                type="date"
                value={filtros.dataInicio}
                onChange={(e) => {
                  const novaData = e.target.value;
                  setFiltros({...filtros, dataInicio: novaData});
                  // Salvar no localStorage para próximas vezes
                  localStorage.setItem('fluxo_caixa_data_inicio', novaData);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Data Fim</label>
              <input
                type="date"
                value={filtros.dataFim}
                onChange={(e) => setFiltros({...filtros, dataFim: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tipo</label>
              <select
                value={filtros.tipo}
                onChange={(e) => setFiltros({...filtros, tipo: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="todos">Todos</option>
                <option value="receita">Receitas</option>
                <option value="despesa">Despesas</option>
              </select>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setShowForm(true)}
                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                + Novo Lançamento
              </button>
              <button
                onClick={() => setShowCategoriaModal(true)}
                className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
                title="Gerenciar Categorias"
              >
                📁
              </button>
            </div>
          </div>
        </div>

        {/* Resumo do período */}
        {relatorio && (
          <div className="bg-white p-6 border-b">
            <h3 className="text-lg font-semibold mb-4">📊 Resumo do Período</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm text-green-600">Total Receitas</p>
                <p className="text-xl font-bold text-green-700">R$ {relatorio.total_receitas.toFixed(2).replace('.', ',')}</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg">
                <p className="text-sm text-red-600">Total Despesas</p>
                <p className="text-xl font-bold text-red-700">R$ {relatorio.total_despesas.toFixed(2).replace('.', ',')}</p>
              </div>
              <div className={`p-4 rounded-lg ${relatorio.saldo_periodo >= 0 ? 'bg-blue-50' : 'bg-orange-50'}`}>
                <p className={`text-sm ${relatorio.saldo_periodo >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>Saldo Período</p>
                <p className={`text-xl font-bold ${relatorio.saldo_periodo >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>
                  R$ {relatorio.saldo_periodo.toFixed(2).replace('.', ',')}
                </p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <p className="text-sm text-purple-600">Saldo Final</p>
                <p className="text-xl font-bold text-purple-700">R$ {relatorio.saldo_final.toFixed(2).replace('.', ',')}</p>
              </div>
            </div>
          </div>
        )}

        {/* Lista de Lançamentos */}
        <div className="bg-white rounded-b-lg shadow-lg">
          {loading ? (
            <div className="p-8 text-center">
              <div className="text-4xl mb-4">🔄</div>
              <p className="text-gray-600">Carregando lançamentos...</p>
            </div>
          ) : lancamentosFiltrados.length === 0 ? (
            <div className="p-8 text-center">
              <div className="text-4xl mb-4">📝</div>
              <p className="text-gray-600 text-lg">Nenhum lançamento encontrado</p>
              <p className="text-gray-500 text-sm mt-2">Adicione seu primeiro lançamento no fluxo de caixa</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descrição</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoria</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Valor</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Ações</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {lancamentosFiltrados.map((lancamento) => (
                    <tr key={lancamento.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 text-sm text-gray-900">
                        {new Date(lancamento.data + 'T00:00:00').toLocaleDateString('pt-BR')}
                      </td>
                      <td className="px-4 py-4">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          lancamento.tipo === 'receita'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {lancamento.tipo === 'receita' ? '💰 Receita' : '💸 Despesa'}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">{lancamento.descricao}</td>
                      <td className="px-4 py-4 text-sm text-gray-500">{lancamento.categoria}</td>
                      <td className={`px-4 py-4 text-center text-sm font-medium ${
                        lancamento.tipo === 'receita' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {lancamento.tipo === 'receita' ? '+' : '-'}R$ {lancamento.valor.toFixed(2).replace('.', ',')}
                      </td>
                      <td className="px-4 py-4 text-center">
                        <div className="flex justify-center space-x-2">
                          <button
                            onClick={() => editarLancamento(lancamento)}
                            className="px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
                          >
                            Editar
                          </button>
                          <button
                            onClick={() => excluirLancamento(lancamento.id)}
                            className="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600"
                          >
                            Excluir
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal do Formulário */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-screen overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">
              {editandoLancamento ? 'Editar Lançamento' : 'Novo Lançamento'}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tipo</label>
                <select
                  value={formData.tipo}
                  onChange={(e) => setFormData({...formData, tipo: e.target.value, categoria: ''})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="receita">💰 Receita</option>
                  <option value="despesa">💸 Despesa</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Descrição</label>
                <input
                  type="text"
                  value={formData.descricao}
                  onChange={(e) => setFormData({...formData, descricao: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  placeholder="Ex: Oferta do domingo"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Categoria</label>
                <select
                  value={formData.categoria}
                  onChange={(e) => setFormData({...formData, categoria: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Selecione uma categoria</option>
                  {categorias[formData.tipo]?.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Valor</label>
                <input
                  type="text"
                  value={formData.valor}
                  onChange={(e) => {
                    const valor = e.target.value.replace(/[^\d,]/g, '');
                    setFormData({...formData, valor});
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  placeholder="0,00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Data</label>
                <input
                  type="date"
                  value={formData.data}
                  onChange={(e) => setFormData({...formData, data: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Método de Pagamento</label>
                <select
                  value={formData.metodo_pagamento}
                  onChange={(e) => setFormData({...formData, metodo_pagamento: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="dinheiro">💵 Dinheiro</option>
                  <option value="pix">📱 PIX</option>
                  <option value="cartao_debito">💳 Cartão Débito</option>
                  <option value="cartao_credito">💳 Cartão Crédito</option>
                  <option value="transferencia">🏦 Transferência</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Observações</label>
                <textarea
                  value={formData.observacoes}
                  onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                  placeholder="Informações adicionais..."
                />
              </div>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditandoLancamento(null);
                    setFormData({
                      tipo: 'receita',
                      descricao: '',
                      categoria: '',
                      valor: '',
                      data: new Date().toISOString().split('T')[0],
                      metodo_pagamento: 'dinheiro',
                      observacoes: ''
                    });
                  }}
                  className="flex-1 px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
                >
                  {loading ? 'Salvando...' : editandoLancamento ? 'Atualizar' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Gerenciar Categorias */}
      {showCategoriaModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-screen overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">📁 Gerenciar Categorias</h3>
              <button
                onClick={() => setShowCategoriaModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            {/* Formulário para nova categoria */}
            <form onSubmit={handleSalvarCategoria} className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium mb-3">➕ Nova Categoria</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                  <input
                    type="text"
                    value={categoriaForm.nome}
                    onChange={(e) => setCategoriaForm({...categoriaForm, nome: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                  <select
                    value={categoriaForm.tipo}
                    onChange={(e) => setCategoriaForm({...categoriaForm, tipo: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="receita">Receita</option>
                    <option value="despesa">Despesa</option>
                    <option value="ambos">Ambos</option>
                  </select>
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrição (opcional)</label>
                <input
                  type="text"
                  value={categoriaForm.descricao}
                  onChange={(e) => setCategoriaForm({...categoriaForm, descricao: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <button
                type="submit"
                className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
              >
                Criar Categoria
              </button>
            </form>

            {/* Lista de categorias existentes */}
            <div>
              <h4 className="font-medium mb-3">📋 Categorias Existentes</h4>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {categoriasDisponiveis.map(categoria => (
                  <div key={categoria.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-md">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{categoria.nome}</span>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          categoria.tipo === 'receita' ? 'bg-green-100 text-green-700' :
                          categoria.tipo === 'despesa' ? 'bg-red-100 text-red-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {categoria.tipo}
                        </span>
                      </div>
                      {categoria.descricao && (
                        <p className="text-sm text-gray-600">{categoria.descricao}</p>
                      )}
                    </div>
                    <button
                      onClick={() => handleExcluirCategoria(categoria.id)}
                      className="text-red-500 hover:text-red-700 ml-2"
                      title="Excluir categoria"
                    >
                      🗑️
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FluxoCaixa;
